import { cx } from "@emotion/css";
import { Plus, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
	OutlinedPrimaryButton,
	PrimaryButton,
	TextPrimaryButton,
} from "src/components/Default/LekUI/Button";
import {
	Modal,
	ModalHeader,
	ModalStickyFooter,
} from "src/components/Modals/Components/ModalsComponents";
import { DecksImportModal } from "src/components/Modals/DecksImportModal/DecksImportModal";
import { EditableFlashcardCard } from "src/components/NewComponents/Flashcards/EditableFlashcardCard";
import {
	FlashcardEditor,
	type FlashcardEditorHandle,
} from "src/components/NewComponents/Flashcards/FlashcardEditor";
import { useAddFlashcards } from "src/hooks/useFlashcards";
import { useFontSize } from "src/hooks/useFontSize";
import FlashcardIcon from "src/images/Icons/newIcons/searchTypes/fish.svg";

interface AddFlashcardModalProps {
	deckCode: string;
	onClose: () => void;
}

interface StagedFlashcard {
	key: number;
	front: string;
	back: string;
}

export function AddFlashcardModal({
	deckCode,
	onClose,
}: AddFlashcardModalProps) {
	const { t } = useTranslation();
	const { modalTextFontSizeClass } = useFontSize();
	const editorRef = useRef<FlashcardEditorHandle>(null);
	const keyCounter = useRef(0);

	const { mutateAsync: addFlashcards, isPending: isSaving } =
		useAddFlashcards();

	const [flashcards, setFlashcards] = useState<StagedFlashcard[]>([]);
	const [editorKey, setEditorKey] = useState(0);
	const [editingKey, setEditingKey] = useState<number | null>(null);
	const [isImportOpen, setIsImportOpen] = useState(false);
	const [isEditorValid, setIsEditorValid] = useState(false);

	const nextKey = () => {
		keyCounter.current += 1;
		return keyCounter.current;
	};

	const showError = () => {
		toast.error(t("errors.somethingWentWrong", "Coś poszło nie tak"), {
			position: toast.POSITION.BOTTOM_LEFT,
			pauseOnHover: false,
			autoClose: 3000,
		});
	};

	const showSuccess = () => {
		toast.success(t("decks.flashcardsAdded", "Dodano do zestawu"), {
			position: toast.POSITION.BOTTOM_LEFT,
			pauseOnHover: false,
			autoClose: 2000,
		});
	};

	const handleAdd = (next: { front: string; back: string }) => {
		setFlashcards((prev) => [
			...prev,
			{ key: nextKey(), front: next.front, back: next.back },
		]);
		// Reset edytora — bieżąca fiszka przechodzi do listy powyżej,
		// a poniżej pojawia się czysty edytor na kolejną.
		setEditorKey((key) => key + 1);
	};

	const handleImported = (imported: { front: string; back: string }[]) => {
		if (!imported.length) {
			return;
		}
		setFlashcards((prev) => [
			...prev,
			...imported.map((f) => ({
				key: nextKey(),
				front: f.front,
				back: f.back,
			})),
		]);
	};

	const handleEditCard = (
		key: number,
		next: { front: string; back: string },
	) => {
		setFlashcards((prev) =>
			prev.map((f) =>
				f.key === key ? { ...f, front: next.front, back: next.back } : f,
			),
		);
		setEditingKey(null);
	};

	const handleDeleteCard = (key: number) => {
		setFlashcards((prev) => prev.filter((f) => f.key !== key));
	};

	// Wypełniony edytor liczy się do zapisu — nie trzeba najpierw klikać "Dodaj kolejną".
	const toSaveCount = flashcards.length + (isEditorValid ? 1 : 0);

	const handleSave = async () => {
		const pending = editorRef.current?.getValidValues();
		const all = pending ? [...flashcards, pending] : flashcards;
		if (!all.length) {
			return;
		}
		try {
			await addFlashcards({
				code: deckCode,
				flashcards: all.map((f) => ({ front: f.front, back: f.back })),
			});
			showSuccess();
			onClose();
		} catch {
			showError();
		}
	};

	return (
		<Modal onClose={onClose} maxWidth="md">
			<ModalHeader
				Icon={FlashcardIcon}
				title={t("modals.addFlashcard.title", "Dodaj fiszkę")}
				hasCloseButton
			/>
			<div className="flex flex-col gap-20 mt-24 deck-preview-list">
				<div className="flex items-center justify-between gap-16 pb-16 border-bottom-1 flex-col-mobile">
					<span className={cx(modalTextFontSizeClass, "text-dark-grey")}>
						{t(
							"modals.addFlashcard.description",
							"Wypełnij obie strony. Możesz dodać kilka fiszek naraz i zapisać je jednym przyciskiem.",
						)}
					</span>
					<OutlinedPrimaryButton
						className="flex items-center gap-8 flex-shrink-0"
						onClick={() => setIsImportOpen(true)}
						disabled={isSaving}
						data-test-id="add-flashcard-modal-import"
					>
						<Upload className="icon-super-small" />
						<span>
							{t("modals.addFlashcard.importFromFile", "Importuj z pliku")}
						</span>
					</OutlinedPrimaryButton>
				</div>

				{flashcards.length > 0 && (
					<div className="flex flex-col gap-12">
						{flashcards.map((flashcard) => (
							<EditableFlashcardCard
								key={flashcard.key}
								flashcard={flashcard}
								isEditing={editingKey === flashcard.key}
								onStartEdit={() => setEditingKey(flashcard.key)}
								onCancelEdit={() => setEditingKey(null)}
								onSave={(next) => handleEditCard(flashcard.key, next)}
								onDelete={() => handleDeleteCard(flashcard.key)}
								testId={`add-flashcard-modal-card-${flashcard.key}`}
							/>
						))}
					</div>
				)}

				<FlashcardEditor
					key={`add-editor-${editorKey}`}
					ref={editorRef}
					bare
					hideFooter
					keySuffix={`add-modal-${editorKey}`}
					onSave={handleAdd}
					onCancel={onClose}
					onValidChange={setIsEditorValid}
					testIdPrefix="add-flashcard-modal"
				/>
				<TextPrimaryButton
					className="flashcard--add-another-button w-100 flex items-center gap-8"
					onClick={() => editorRef.current?.save()}
					disabled={isSaving || !isEditorValid}
					data-test-id="add-flashcard-modal-add"
				>
					<Plus className="icon-super-small" />
					<span>
						{t("modals.addFlashcard.addAnother", "Dodaj kolejną fiszkę")}
					</span>
				</TextPrimaryButton>
			</div>
			<ModalStickyFooter className="justify-between gap-12 mt-24 pt-16 border-top-1">
				<span className={cx(modalTextFontSizeClass, "text-grey")}>
					{t("modals.addFlashcard.toSave", "Do zapisania:")}{" "}
					<strong className="text-black">
						{t("modals.addFlashcard.count", {
							count: toSaveCount,
							defaultValue: "{{count}} fiszek",
						})}
					</strong>
				</span>
				<div className="flex items-center gap-12">
					<TextPrimaryButton
						onClick={onClose}
						disabled={isSaving}
						data-test-id="add-flashcard-modal-cancel"
					>
						{t("commands.cancel")}
					</TextPrimaryButton>
					<PrimaryButton
						onClick={handleSave}
						disabled={isSaving || toSaveCount === 0}
						data-test-id="add-flashcard-modal-save"
					>
						{t("commands.save")}
					</PrimaryButton>
				</div>
			</ModalStickyFooter>

			{isImportOpen && (
				<DecksImportModal
					onClose={() => setIsImportOpen(false)}
					onConfirm={handleImported}
				/>
			)}
		</Modal>
	);
}
